import React, { Component } from 'react';
import ReactDOM from 'react-dom';
import isEqual from 'lodash/isEqual';
import classNames from 'classnames';

import { types as sdkTypes } from '../../../util/sdkLoader';
import { parse } from '../../../util/urlHelpers';
import { propTypes } from '../../../util/types';
import { ensureListing } from '../../../util/data';
import { sdkBoundsToFixedCoordinates, hasSameSDKBounds } from '../../../util/maps';

import SearchMapPriceLabel from '../SearchMapPriceLabel/SearchMapPriceLabel';
import SearchMapInfoCard from '../SearchMapInfoCard/SearchMapInfoCard';
import SearchMapGroupLabel from '../SearchMapGroupLabel/SearchMapGroupLabel';
import { groupedByCoordinates, reducedToArray } from './SearchMap.helpers';
import css from './SearchMapWithMapbox.module.css';

export const LABEL_HANDLE = 'SearchMapLabel';
export const INFO_CARD_HANDLE = 'SearchMapInfoCard';
export const SOURCE_AUTOCOMPLETE = 'autocomplete';
const BOUNDS_FIXED_PRECISION = 8;

// Default map center (Nevada County, CA) and zoom when no location is selected
const DEFAULT_CENTER = [-121.0179, 39.3010]; // [lng, lat]
const DEFAULT_ZOOM = 7; // Shows ~1/3 of California on initial load

// ============================================
// ZOOM GATE & PERFORMANCE CONFIGURATION
// ============================================
const MIN_ZOOM_TO_SEARCH = 8; // Show listings when ~250 mile radius visible
const MAX_LISTINGS_PER_PAGE = 250; // Server-side cap for listings per request
const CLUSTER_RADIUS = 50; // Cluster radius in pixels
const CLUSTER_MAX_ZOOM = 14; // Max zoom to cluster points

// Map style configuration
const STYLE_KEY = 'cafiredefense_map_style';
const MAP_STYLES = {
  streets: 'mapbox://styles/mapbox/streets-v12',
  topo: 'mapbox://styles/mapbox/outdoors-v12',
  satellite: 'mapbox://styles/mapbox/satellite-streets-v12',
};
const DEFAULT_STYLE = 'topo';

// GeoJSON source names
const LISTINGS_SOURCE = 'listings-source';
const CLUSTERS_LAYER = 'clusters';
const CLUSTER_COUNT_LAYER = 'cluster-count';
const UNCLUSTERED_POINT_LAYER = 'unclustered-point';

// Helper to get saved style from localStorage
const getSavedMapStyle = () => {
  if (typeof window === 'undefined') return DEFAULT_STYLE;
  const saved = window.localStorage.getItem(STYLE_KEY);
  return saved && MAP_STYLES[saved] ? saved : DEFAULT_STYLE;
};

// Helper to save style to localStorage
const saveMapStyle = (styleKey) => {
  if (typeof window !== 'undefined') {
    window.localStorage.setItem(STYLE_KEY, styleKey);
  }
};

// IP Geolocation - cache key and helpers
const USER_LOCATION_KEY = 'cafiredefense_user_location';
const LOCATION_CACHE_DURATION = 24 * 60 * 60 * 1000; // 24 hours

// Get cached user location from localStorage
const getCachedUserLocation = () => {
  if (typeof window === 'undefined') return null;
  try {
    const cached = window.localStorage.getItem(USER_LOCATION_KEY);
    if (!cached) return null;
    const { lat, lng, timestamp } = JSON.parse(cached);
    // Check if cache is still valid
    if (Date.now() - timestamp < LOCATION_CACHE_DURATION) {
      return [lng, lat]; // Mapbox uses [lng, lat]
    }
    return null;
  } catch {
    return null;
  }
};

// Save user location to localStorage
const cacheUserLocation = (lat, lng) => {
  if (typeof window !== 'undefined') {
    window.localStorage.setItem(USER_LOCATION_KEY, JSON.stringify({
      lat,
      lng,
      timestamp: Date.now(),
    }));
  }
};

// Fetch user location from IP (uses free ip-api.com service)
const fetchUserLocationFromIP = async () => {
  try {
    const response = await fetch('http://ip-api.com/json/?fields=lat,lon,status');
    const data = await response.json();
    if (data.status === 'success') {
      cacheUserLocation(data.lat, data.lon);
      return [data.lon, data.lat]; // Mapbox uses [lng, lat]
    }
    return null;
  } catch {
    return null;
  }
};

// Map Style Toggle Component
const MapStyleToggle = ({ activeStyle, onStyleChange }) => {
  const styles = [
    { key: 'streets', label: 'Streets' },
    { key: 'topo', label: 'Topo' },
    { key: 'satellite', label: 'Satellite' },
  ];

  return (
    <div className={css.styleToggle}>
      {styles.map(({ key, label }) => (
        <button
          key={key}
          type="button"
          className={classNames(css.styleButton, {
            [css.styleButtonActive]: activeStyle === key,
          })}
          onClick={() => onStyleChange(key)}
        >
          {label}
        </button>
      ))}
    </div>
  );
};

// Zoom In Overlay Component
const ZoomInOverlay = ({ minZoom }) => (
  <div className={css.zoomOverlay}>
    <div className={css.zoomOverlayContent}>
      <span className={css.zoomIcon}>🔍</span>
      <p className={css.zoomMessage}>Zoom in to see requests</p>
      <p className={css.zoomHint}>Current area is too large to display listings</p>
    </div>
  </div>
);

// Too Many Results Overlay Component
const TooManyResultsOverlay = ({ count, cap }) => (
  <div className={css.tooManyOverlay}>
    <div className={css.tooManyContent}>
      <span className={css.tooManyIcon}>⚠️</span>
      <p className={css.tooManyMessage}>Too many requests in this area</p>
      <p className={css.tooManyHint}>
        Showing {cap} of {count}+ results. Zoom in or apply filters.
      </p>
    </div>
  </div>
);

const { LatLng: SDKLatLng, LatLngBounds: SDKLatLngBounds } = sdkTypes;

/**
 * Fit part of map (descriped with bounds) to visible map-viewport
 *
 * @param {Object} map - map that needs to be centered with given bounds
 * @param {SDK.LatLngBounds} bounds - the area that needs to be visible when map loads.
 */
export const fitMapToBounds = (map, bounds, options) => {
  const { padding = 0, isAutocompleteSearch = false } = options;

  // map bounds as string literal for google.maps
  const mapBounds = sdkBoundsToMapboxBounds(bounds);
  const paddingOptionMaybe = padding == null ? { padding } : {};
  const eventData = isAutocompleteSearch ? { searchSource: SOURCE_AUTOCOMPLETE } : {};

  // If bounds are given, use it (defaults to center & zoom).
  if (map && mapBounds) {
    map.fitBounds(mapBounds, { ...paddingOptionMaybe, linear: true, duration: 0 }, eventData);
  }
};

/**
 * Convert Mapbox formatted LatLng object to Sharetribe SDK's LatLng coordinate format
 * Longitudes > 180 and < -180 are converted to the correct corresponding value
 * between -180 and 180.
 *
 * @param {LngLat} mapboxLngLat - Mapbox LngLat
 *
 * @return {SDKLatLng} - Converted latLng coordinate
 */
export const mapboxLngLatToSDKLatLng = lngLat => {
  const mapboxLng = lngLat.lng;

  // For bounding boxes that overlap the antimeridian Mapbox sometimes gives
  // longitude values outside -180 and 180 degrees.Those values are converted
  // so that longitude is always between -180 and 180.
  const lng = mapboxLng > 180 ? mapboxLng - 360 : mapboxLng < -180 ? mapboxLng + 360 : mapboxLng;

  return new SDKLatLng(lngLat.lat, lng);
};

/**
 * Convert Mapbox formatted bounds object to Sharetribe SDK's bounds format
 *
 * @param {LngLatBounds} mapboxBounds - Mapbox LngLatBounds
 *
 * @return {SDKLatLngBounds} - Converted bounds
 */
export const mapboxBoundsToSDKBounds = mapboxBounds => {
  if (!mapboxBounds) {
    return null;
  }

  const ne = mapboxBounds.getNorthEast();
  const sw = mapboxBounds.getSouthWest();
  return new SDKLatLngBounds(mapboxLngLatToSDKLatLng(ne), mapboxLngLatToSDKLatLng(sw));
};

/**
 * Convert sdk bounds that overlap the antimeridian into values that can
 * be passed to Mapbox. This is achieved by converting the SW longitude into
 * a value less than -180 that flows over the antimeridian.
 *
 * @param {SDKLatLng} bounds - bounds passed to the map
 *
 * @return {LngLatBoundsLike} a bounding box that is compatible with Mapbox
 */
const sdkBoundsToMapboxBounds = bounds => {
  if (!bounds) {
    return null;
  }
  const { ne, sw } = bounds;

  // if sw lng is > ne lng => the bounds overlap antimeridian
  // => flip the nw lng to the negative side so that the value
  // is less than -180
  const swLng = sw.lng > ne.lng ? -360 + sw.lng : sw.lng;

  return [[swLng, sw.lat], [ne.lng, ne.lat]];
};

/**
 * Return map bounds as SDKBounds
 *
 * @param {Mapbox} map - Mapbox map from where the bounds are asked
 *
 * @return {SDKLatLngBounds} - Converted bounds of given map
 */
export const getMapBounds = map => mapboxBoundsToSDKBounds(map.getBounds());

/**
 * Return map center as SDKLatLng
 *
 * @param {Mapbox} map - Mapbox map from where the center is asked
 *
 * @return {SDKLatLng} - Converted center of given map
 */
export const getMapCenter = map => mapboxLngLatToSDKLatLng(map.getCenter());

/**
 * Check if map library is loaded
 */
export const isMapsLibLoaded = () =>
  typeof window !== 'undefined' && window.mapboxgl && window.mapboxgl;

/**
 * Convert listings to GeoJSON FeatureCollection for clustering
 */
const listingsToGeoJSON = (listings) => {
  const features = listings
    .filter(l => l.attributes?.geolocation)
    .map(listing => ({
      type: 'Feature',
      id: listing.id.uuid,
      geometry: {
        type: 'Point',
        coordinates: [listing.attributes.geolocation.lng, listing.attributes.geolocation.lat],
      },
      properties: {
        id: listing.id.uuid,
        title: listing.attributes.title || '',
        price: listing.attributes.price?.amount || 0,
        currency: listing.attributes.price?.currency || 'USD',
      },
    }));

  return {
    type: 'FeatureCollection',
    features,
  };
};

/**
 * Return price labels grouped by listing locations.
 * This is a helper function for SearchMapWithMapbox component.
 */
const priceLabelsInLocations = (
  listings,
  activeListingId,
  infoCardOpen,
  onListingClicked,
  mapComponentRefreshToken
) => {
  const listingArraysInLocations = reducedToArray(groupedByCoordinates(listings));
  const priceLabels = listingArraysInLocations.reverse().map(listingArr => {
    const isActive = activeListingId
      ? !!listingArr.find(l => activeListingId.uuid === l.id.uuid)
      : false;

    // If location contains only one listing, print price label
    if (listingArr.length === 1) {
      const listing = listingArr[0];
      const infoCardOpenIds = Array.isArray(infoCardOpen)
        ? infoCardOpen.map(l => l.id.uuid)
        : infoCardOpen
        ? [infoCardOpen.id.uuid]
        : [];

      // if the listing is open, don't print price label
      if (infoCardOpen != null && infoCardOpenIds.includes(listing.id.uuid)) {
        return null;
      }

      // Explicit type change to object literal for Google OverlayViews (geolocation is SDK type)
      const { geolocation } = listing.attributes;

      const key = listing.id.uuid;
      return {
        markerId: `price_${key}`,
        location: geolocation,
        type: 'price',
        componentProps: {
          key,
          isActive,
          className: LABEL_HANDLE,
          listing,
          onListingClicked,
          mapComponentRefreshToken,
        },
      };
    }

    // Explicit type change to object literal for Google OverlayViews (geolocation is SDK type)
    const firstListing = ensureListing(listingArr[0]);
    const geolocation = firstListing.attributes.geolocation;

    const key = listingArr[0].id.uuid;
    return {
      markerId: `group_${key}`,
      location: geolocation,
      type: 'group',
      componentProps: {
        key,
        isActive,
        className: LABEL_HANDLE,
        listings: listingArr,
        onListingClicked,
        mapComponentRefreshToken,
      },
    };
  });
  return priceLabels;
};

/**
 * Return info card. This is a helper function for SearchMapWithMapbox component.
 */
const infoCardComponent = (
  infoCardOpen,
  onListingInfoCardClicked,
  createURLToListing,
  mapComponentRefreshToken
) => {
  const listingsArray = Array.isArray(infoCardOpen) ? infoCardOpen : [infoCardOpen];

  if (!infoCardOpen) {
    return null;
  }

  const firstListing = ensureListing(listingsArray[0]);
  const key = firstListing.id.uuid;
  const geolocation = firstListing.attributes.geolocation;

  return {
    markerId: `infoCard_${key}`,
    location: geolocation,
    componentProps: {
      key,
      mapComponentRefreshToken,
      className: INFO_CARD_HANDLE,
      listings: listingsArray,
      onListingInfoCardClicked,
      createURLToListing,
    },
  };
};

/**
 * SearchMap component using Mapbox as map provider
 *
 * @component
 * @param {Object} props
 * @param {string} [props.id] - The ID
 * @param {string} [props.className] - Custom class that extends the default class for the root element
 * @param {propTypes.latlngBounds} [props.bounds] - The bounds
 * @param {propTypes.latlng} [props.center] - The center
 * @param {Object} props.location - The location
 * @param {string} props.location.search - The search query params
 * @param {propTypes.uuid} [props.activeListingId] - The active listing ID
 * @param {Array<propTypes.listing>} [props.listings] - The listings
 * @param {Function} props.onMapMoveEnd - The function to move end
 * @param {Function} props.onMapLoad - The function to load
 * @param {number} [props.zoom] - The zoom
 * @param {string} [props.reusableMapHiddenHandle] - The handle for the reusable map hidden
 * @param {Object} props.config - The configuration
 * @param {Object} [props.pagination] - Pagination info (totalItems, totalPages)
 * @returns {JSX.Element}
 */
class SearchMapWithMapbox extends Component {
  constructor(props) {
    super(props);
    this.map = typeof window !== 'undefined' && window.mapboxMap ? window.mapboxMap : null;
    this.currentMarkers = [];
    this.currentInfoCard = null;
    this.state = {
      mapContainer: null,
      isMapReady: false,
      activeStyle: getSavedMapStyle(),
      currentZoom: DEFAULT_ZOOM,
      isBelowMinZoom: DEFAULT_ZOOM < MIN_ZOOM_TO_SEARCH,
    };
    this.viewportBounds = null;

    this.onMount = this.onMount.bind(this);
    this.onMoveend = this.onMoveend.bind(this);
    this.onZoom = this.onZoom.bind(this);
    this.initializeMap = this.initializeMap.bind(this);
    this.handleDoubleClickOnInfoCard = this.handleDoubleClickOnInfoCard.bind(this);
    this.handleMobilePinchZoom = this.handleMobilePinchZoom.bind(this);
    this.handleStyleChange = this.handleStyleChange.bind(this);
    this.reAddMarkers = this.reAddMarkers.bind(this);
    this.setupClusterSource = this.setupClusterSource.bind(this);
    this.updateClusterSource = this.updateClusterSource.bind(this);
    this.handleClusterClick = this.handleClusterClick.bind(this);
  }

  componentDidUpdate(prevProps) {
    if (!isEqual(prevProps.location, this.props.location)) {
      // If no mapSearch url parameter is given, this is original location search
      const { mapSearch } = parse(this.props.location.search, {
        latlng: ['origin'],
        latlngBounds: ['bounds'],
      });
      if (!mapSearch) {
        this.viewportBounds = null;
      }
    }

    if (this.map) {
      const currentBounds = getMapBounds(this.map);

      // Do not call fitMapToBounds if bounds are the same.
      // Our bounds are viewport bounds, and fitBounds will try to add margins around those bounds
      // that would result to zoom-loop (bound change -> fitmap -> bounds change -> ...)
      if (!isEqual(this.props.bounds, currentBounds) && !this.viewportBounds) {
        fitMapToBounds(this.map, this.props.bounds, { padding: 0, isAutocompleteSearch: true });
      }

      // Update cluster source when listings change
      if (prevProps.listings !== this.props.listings) {
        this.updateClusterSource(this.props.listings);
      }
    }

    if (!this.map && this.state.mapContainer) {
      this.initializeMap();

      /* Notify parent component that Mapbox map is loaded */
      this.props.onMapLoad(this.map);
    } else if (prevProps.mapComponentRefreshToken !== this.props.mapComponentRefreshToken) {
      /* Notify parent component that Mapbox map is loaded */
      this.map.resize();
      this.props.onMapLoad(this.map);
    }
  }

  componentWillUnmount() {
    this.currentInfoCard?.markerContainer?.removeEventListener(
      'dblclick',
      this.handleDoubleClickOnInfoCard
    );
    document.removeEventListener('gesturestart', this.handleMobilePinchZoom, false);
    document.removeEventListener('gesturechange', this.handleMobilePinchZoom, false);
    document.removeEventListener('gestureend', this.handleMobilePinchZoom, false);
  }

  onMount(element) {
    // This prevents pinch zoom to affect whole page on mobile Safari.
    document.addEventListener('gesturestart', this.handleMobilePinchZoom, false);
    document.addEventListener('gesturechange', this.handleMobilePinchZoom, false);
    document.addEventListener('gestureend', this.handleMobilePinchZoom, false);

    this.setState({ mapContainer: element });
  }

  onZoom() {
    if (this.map) {
      const zoom = this.map.getZoom();
      const isBelowMinZoom = zoom < MIN_ZOOM_TO_SEARCH;

      if (isBelowMinZoom !== this.state.isBelowMinZoom) {
        this.setState({ currentZoom: zoom, isBelowMinZoom });
      } else if (Math.abs(zoom - this.state.currentZoom) > 0.5) {
        this.setState({ currentZoom: zoom });
      }
    }
  }

  onMoveend(e) {
    if (this.map) {
      // If reusableMapHiddenHandle is given and parent element has that class,
      // we don't listen moveend events.
      // This fixes mobile Chrome bug that sends map events to invisible map components.
      const isHiddenByReusableMap =
        this.props.reusableMapHiddenHandle &&
        this.state.mapContainer.parentElement.classList.contains(
          this.props.reusableMapHiddenHandle
        );
      if (!isHiddenByReusableMap) {
        const currentZoom = this.map.getZoom();
        const isBelowMinZoom = currentZoom < MIN_ZOOM_TO_SEARCH;

        // Update zoom state
        this.setState({ currentZoom, isBelowMinZoom });

        // ZOOM GATE: Don't trigger search if below min zoom
        if (isBelowMinZoom) {
          // Clear markers when zoomed out
          this.clearAllMarkers();
          return;
        }

        const viewportMapBounds = getMapBounds(this.map);
        const viewportMapCenter = getMapCenter(this.map);
        const viewportBounds = sdkBoundsToFixedCoordinates(
          viewportMapBounds,
          BOUNDS_FIXED_PRECISION
        );

        // ViewportBounds from (previous) rendering differ from viewportBounds currently set to map
        // I.e. user has changed the map somehow: moved, panned, zoomed, resized
        const viewportBoundsChanged =
          this.viewportBounds && !hasSameSDKBounds(this.viewportBounds, viewportBounds);

        // Pass zoom level to parent for additional validation
        this.props.onMapMoveEnd(viewportBoundsChanged, {
          viewportBounds,
          viewportMapCenter,
          zoom: currentZoom,
          isBelowMinZoom,
        });
        this.viewportBounds = viewportBounds;
      }
    }
  }

  clearAllMarkers() {
    // Remove all DOM markers
    this.currentMarkers.forEach(m => {
      if (m.marker) {
        m.marker.remove();
      }
    });
    this.currentMarkers = [];

    // Remove info card
    if (this.currentInfoCard?.marker) {
      this.currentInfoCard.marker.remove();
      this.currentInfoCard = null;
    }

    // Clear cluster source
    if (this.map?.getSource(LISTINGS_SOURCE)) {
      this.map.getSource(LISTINGS_SOURCE).setData({
        type: 'FeatureCollection',
        features: [],
      });
    }
  }

  setupClusterSource() {
    if (!this.map) return;

    // Check if source already exists
    if (this.map.getSource(LISTINGS_SOURCE)) return;

    // Add empty GeoJSON source with clustering enabled
    this.map.addSource(LISTINGS_SOURCE, {
      type: 'geojson',
      data: {
        type: 'FeatureCollection',
        features: [],
      },
      cluster: true,
      clusterMaxZoom: CLUSTER_MAX_ZOOM,
      clusterRadius: CLUSTER_RADIUS,
    });

    // Cluster circles layer
    this.map.addLayer({
      id: CLUSTERS_LAYER,
      type: 'circle',
      source: LISTINGS_SOURCE,
      filter: ['has', 'point_count'],
      paint: {
        'circle-color': [
          'step',
          ['get', 'point_count'],
          '#51bbd6', // Blue for small clusters
          10,
          '#f1f075', // Yellow for medium clusters
          30,
          '#f28cb1', // Pink for large clusters
        ],
        'circle-radius': [
          'step',
          ['get', 'point_count'],
          20,
          10,
          30,
          30,
          40,
        ],
        'circle-stroke-width': 2,
        'circle-stroke-color': '#fff',
      },
    });

    // Cluster count text layer
    this.map.addLayer({
      id: CLUSTER_COUNT_LAYER,
      type: 'symbol',
      source: LISTINGS_SOURCE,
      filter: ['has', 'point_count'],
      layout: {
        'text-field': ['get', 'point_count_abbreviated'],
        'text-font': ['DIN Offc Pro Medium', 'Arial Unicode MS Bold'],
        'text-size': 12,
      },
      paint: {
        'text-color': '#000',
      },
    });

    // Unclustered point layer (individual listings at high zoom)
    this.map.addLayer({
      id: UNCLUSTERED_POINT_LAYER,
      type: 'circle',
      source: LISTINGS_SOURCE,
      filter: ['!', ['has', 'point_count']],
      paint: {
        'circle-color': '#11b4da',
        'circle-radius': 8,
        'circle-stroke-width': 2,
        'circle-stroke-color': '#fff',
      },
    });

    // Click handler for clusters - zoom in
    this.map.on('click', CLUSTERS_LAYER, this.handleClusterClick);

    // Change cursor on hover
    this.map.on('mouseenter', CLUSTERS_LAYER, () => {
      this.map.getCanvas().style.cursor = 'pointer';
    });
    this.map.on('mouseleave', CLUSTERS_LAYER, () => {
      this.map.getCanvas().style.cursor = '';
    });
  }

  handleClusterClick(e) {
    const features = this.map.queryRenderedFeatures(e.point, {
      layers: [CLUSTERS_LAYER],
    });

    if (!features.length) return;

    const clusterId = features[0].properties.cluster_id;
    const source = this.map.getSource(LISTINGS_SOURCE);

    source.getClusterExpansionZoom(clusterId, (err, zoom) => {
      if (err) return;

      this.map.easeTo({
        center: features[0].geometry.coordinates,
        zoom: zoom,
      });
    });
  }

  updateClusterSource(listings) {
    if (!this.map) return;

    // Setup source if not exists
    if (!this.map.getSource(LISTINGS_SOURCE)) {
      this.setupClusterSource();
    }

    const source = this.map.getSource(LISTINGS_SOURCE);
    if (source) {
      const geojson = listingsToGeoJSON(listings || []);
      source.setData(geojson);
    }
  }

  initializeMap() {
    const { offsetHeight, offsetWidth } = this.state.mapContainer;
    const hasDimensions = offsetHeight > 0 && offsetWidth > 0;
    if (hasDimensions) {
      const initialStyle = MAP_STYLES[this.state.activeStyle] || MAP_STYLES[DEFAULT_STYLE];

      // Try to get user's location from cache first
      const cachedLocation = getCachedUserLocation();
      const initialCenter = cachedLocation || DEFAULT_CENTER;

      this.map = new window.mapboxgl.Map({
        container: this.state.mapContainer,
        style: initialStyle,
        center: initialCenter,
        zoom: DEFAULT_ZOOM,
        scrollZoom: true,
      });
      window.mapboxMap = this.map;

      // If no cached location, fetch from IP and update map center
      if (!cachedLocation) {
        fetchUserLocationFromIP().then(location => {
          if (location && this.map) {
            this.map.setCenter(location);
          }
        });
      }

      var nav = new window.mapboxgl.NavigationControl({ showCompass: false });
      this.map.addControl(nav, 'top-left');

      this.map.on('moveend', this.onMoveend);
      this.map.on('zoom', this.onZoom);

      // Re-add markers and clusters after style change (style.load clears all layers)
      this.map.on('style.load', () => {
        this.setupClusterSource();
        this.updateClusterSource(this.props.listings);
        this.reAddMarkers();
      });

      // Setup cluster source after map loads
      this.map.on('load', () => {
        this.setupClusterSource();
        this.updateClusterSource(this.props.listings);
      });

      // Introduce rerendering after map is ready (to include labels),
      // but keep the map out of state life cycle.
      this.setState({ isMapReady: true });
    }
  }

  // Handle map style change from toggle
  handleStyleChange(styleKey) {
    if (!this.map || styleKey === this.state.activeStyle) return;

    // Save to localStorage
    saveMapStyle(styleKey);

    // Update state
    this.setState({ activeStyle: styleKey });

    // Change map style (triggers style.load event which calls reAddMarkers)
    this.map.setStyle(MAP_STYLES[styleKey]);
  }

  // Re-add markers after style change
  reAddMarkers() {
    if (!this.map) return;

    // Remove all current markers first (they're still in DOM but detached from map)
    this.currentMarkers.forEach(m => {
      if (m.marker) {
        m.marker.remove();
      }
    });

    // Re-add markers to the map
    this.currentMarkers = this.currentMarkers.map(m => {
      if (m.location) {
        const marker = new window.mapboxgl.Marker(m.markerContainer, { anchor: 'bottom' })
          .setLngLat([m.location.lng, m.location.lat])
          .addTo(this.map);
        return { ...m, marker };
      }
      return m;
    });

    // Re-add info card marker if exists
    if (this.currentInfoCard && this.currentInfoCard.location) {
      if (this.currentInfoCard.marker) {
        this.currentInfoCard.marker.remove();
      }
      const marker = new window.mapboxgl.Marker(this.currentInfoCard.markerContainer, { anchor: 'bottom' })
        .setLngLat([this.currentInfoCard.location.lng, this.currentInfoCard.location.lat])
        .addTo(this.map);
      this.currentInfoCard = { ...this.currentInfoCard, marker };
    }

    // Force re-render to update React portals
    this.forceUpdate();
  }

  handleMobilePinchZoom(e) {
    e.preventDefault();
    // A hack to prevent pinch zoom gesture in mobile Safari
    // Otherwise, pinch zoom would zoom both map and the document.
    document.body.style.zoom = 0.99;
  }

  handleDoubleClickOnInfoCard(e) {
    e.stopPropagation();
  }

  render() {
    const {
      id = 'searchMap',
      className,
      listings = [],
      activeListingId,
      infoCardOpen,
      onListingClicked,
      onListingInfoCardClicked,
      createURLToListing,
      mapComponentRefreshToken,
      config,
      pagination,
    } = this.props;

    const { isBelowMinZoom } = this.state;

    // Check if we have too many results
    const totalItems = pagination?.totalItems || listings.length;
    const hasTooManyResults = totalItems > MAX_LISTINGS_PER_PAGE;

    // Only render DOM markers when zoomed in enough and not too many results
    const shouldRenderDOMMarkers = !isBelowMinZoom && this.map;

    if (shouldRenderDOMMarkers) {
      // Create markers out of price labels and grouped labels
      const labels = priceLabelsInLocations(
        listings,
        activeListingId,
        infoCardOpen,
        onListingClicked,
        mapComponentRefreshToken
      );

      // If map has moved or info card opened, unnecessary markers need to be removed
      const removableMarkers = this.currentMarkers.filter(
        marker => !labels.some(label => label?.markerId === marker?.markerId)
      );
      removableMarkers.forEach(rm => rm.marker.remove());

      // Helper function to create markers to given container
      const createMarker = (data, markerContainer) =>
        new window.mapboxgl.Marker(markerContainer, { anchor: 'bottom' })
          .setLngLat([data.location.lng, data.location.lat])
          .addTo(this.map);

      // SearchMapPriceLabel and SearchMapGroupLabel:
      // create a new marker or use existing one if markerId is among previously rendered markers
      this.currentMarkers = labels
        .filter(v => v != null)
        .map(m => {
          const existingMarkerId = this.currentMarkers.findIndex(
            marker => m.markerId === marker.markerId && marker.marker
          );

          if (existingMarkerId >= 0) {
            const { marker, markerContainer, ...rest } = this.currentMarkers[existingMarkerId];
            return { ...rest, ...m, markerContainer, marker };
          } else {
            const markerContainer = document.createElement('div');
            markerContainer.setAttribute('id', m.markerId);
            markerContainer.classList.add(css.labelContainer);
            const marker = createMarker(m, markerContainer);
            return { ...m, markerContainer, marker };
          }
        });

      /* Create marker for SearchMapInfoCard component */
      if (infoCardOpen) {
        const infoCard = infoCardComponent(
          infoCardOpen,
          onListingInfoCardClicked,
          createURLToListing,
          mapComponentRefreshToken
        );

        // marker container and its styles
        const infoCardContainer = document.createElement('div');
        infoCardContainer.setAttribute('id', infoCard.markerId);
        infoCardContainer.classList.add(css.infoCardContainer);
        infoCardContainer.addEventListener('dblclick', this.handleDoubleClickOnInfoCard, false);

        this.currentInfoCard = {
          ...infoCard,
          markerContainer: infoCardContainer,
          marker: infoCard ? createMarker(infoCard, infoCardContainer) : null,
        };
      } else {
        if (this.currentInfoCard) {
          this.currentInfoCard.markerContainer.removeEventListener(
            'dblclick',
            this.handleDoubleClickOnInfoCard
          );
        }
        this.currentInfoCard = null;
      }
    }

    const CurrentInfoCardMaybe = props => {
      const { mapContainer, currentInfoCard, config } = props;
      const shouldRender = mapContainer && currentInfoCard;
      const { key, ...componentProps } = shouldRender ? currentInfoCard?.componentProps : {};
      return shouldRender
        ? ReactDOM.createPortal(
            <SearchMapInfoCard key={key} {...componentProps} config={config} />,
            currentInfoCard.markerContainer
          )
        : null;
    };

    return (
      <div
        id={id}
        ref={this.onMount}
        className={classNames(className, css.fullArea)}
        onClick={this.props.onClick}
      >
        {/* Style Toggle */}
        {this.state.isMapReady && (
          <MapStyleToggle
            activeStyle={this.state.activeStyle}
            onStyleChange={this.handleStyleChange}
          />
        )}

        {/* Zoom In Overlay */}
        {this.state.isMapReady && isBelowMinZoom && (
          <ZoomInOverlay minZoom={MIN_ZOOM_TO_SEARCH} />
        )}

        {/* Too Many Results Overlay */}
        {this.state.isMapReady && !isBelowMinZoom && hasTooManyResults && (
          <TooManyResultsOverlay count={totalItems} cap={MAX_LISTINGS_PER_PAGE} />
        )}

        {/* DOM Markers */}
        {shouldRenderDOMMarkers && this.currentMarkers.map(m => {
          const { key, ...compProps } = m.componentProps || {};

          // Remove existing activeLabel classes and add it only to the correct container
          m.markerContainer.classList.remove(css.activeLabel);
          if (activeListingId && activeListingId.uuid === key) {
            m.markerContainer.classList.add(css.activeLabel);
          }

          const isMapReadyForMarkers = this.map && m.markerContainer;
          // DOM node that should be used as portal's root
          const portalDOMContainer = isMapReadyForMarkers
            ? document.getElementById(m.markerContainer.id)
            : null;

          // Create component portals for correct marker containers
          if (isMapReadyForMarkers && m.type === 'price') {
            return ReactDOM.createPortal(
              <SearchMapPriceLabel key={key} {...compProps} config={config} />,
              portalDOMContainer
            );
          } else if (isMapReadyForMarkers && m.type === 'group') {
            return ReactDOM.createPortal(
              <SearchMapGroupLabel key={key} {...compProps} />,
              portalDOMContainer
            );
          }
          return null;
        })}
        <CurrentInfoCardMaybe
          mapContainer={this.state.mapContainer}
          currentInfoCard={this.currentInfoCard}
          config={config}
        />
      </div>
    );
  }
}

// Export constants for use by parent components
export { MIN_ZOOM_TO_SEARCH, MAX_LISTINGS_PER_PAGE };

export default SearchMapWithMapbox;
